import { useState } from "react";
import {
  IonIcon,
  IonBadge,
  IonButton,
  IonSelect,
  IonSelectOption,
  IonToggle,
} from "@ionic/react";
import {
  chevronDownOutline,
  chevronUpOutline,
  trashOutline,
  mailOutline,
  callOutline,
} from "ionicons/icons";
import ProfileImage from "../ProfileImage";
import { isSubscriber, subscribedFlagForSave } from "./memberSections";
import {
  useAzureStorage,
  StorageEndpoint,
} from "../../hooks/useAzureStorage";

import "./MemberCard.css";

const ROLE_OPTIONS = ["Admin", "Editor", "Guest", "Member", "None"];

interface MemberCardProps {
  member: {
    _id: string;
    MemberName?: string;
    RoleLabel?: string;
    ContactPersonalId?: string;
    ContactIdLookup?: string;
    Permission?: { Admin?: boolean };
    /** Set when the person joined themselves rather than being added by an
     *  admin. Never present on a non-admin viewer's payload - the API's
     *  allow-list withholds it - so the card degrades to an ordinary one. */
    Subscribed?: boolean;
    Staff?: boolean;
    Student?: boolean;
    Carer?: boolean;
    Settings?: { Notify?: boolean; Calendar?: boolean };
    /** Contact details surfaced to admins as quick-action icons
     *  (mailto: / tel:). Same fields the portal MemberCard exposes;
     *  populated by the API's $addFields stage on /group/{id}/members
     *  for any member whose ContactPersonal record has the values. */
    Email?: string;
    Phone?: string;
    [key: string]: unknown;
  };
  isAdmin: boolean;
  onRemove?: (memberId: string) => void;
  onSave?: (member: {
    _id: string;
    RoleLabel: string;
    Permission: { Admin: boolean };
    Settings: { Notify: boolean; Calendar: boolean };
    /** Sent as `false` only when this save promotes a subscriber. Omitted
     *  otherwise: papi leaves the stored flag alone when the field is
     *  absent, which is what keeps a notify toggle from converting
     *  somebody as a side effect. */
    Subscribed?: boolean;
  }) => void;
}

function getMemberType(member: {
  Staff?: boolean;
  Student?: boolean;
  Carer?: boolean;
}): string {
  const parts: string[] = [];
  if (member.Staff) parts.push("Staff");
  if (member.Student) parts.push("Student");
  if (member.Carer) parts.push("Carer");
  return parts.join(" / ") || "";
}

const MemberCard: React.FC<MemberCardProps> = ({
  member,
  isAdmin,
  onRemove,
  onSave,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  // Local editable state to prevent stale-prop clobbering on rapid edits
  const [localRole, setLocalRole] = useState(member.RoleLabel || "Member");
  const [localNotify, setLocalNotify] = useState(member.Settings?.Notify ?? false);
  const [localCalendar, setLocalCalendar] = useState(member.Settings?.Calendar ?? false);

  const contactId = member.ContactIdLookup || member.ContactPersonalId || "";
  const memberType = getMemberType(member);

  const { storageDetail } = useAzureStorage(
    StorageEndpoint.profile,
    contactId ? `${contactId}.jpg` : "",
    false
  );

  const saveMember = async (
    role: string,
    notify: boolean,
    calendar: boolean
  ) => {
    setSaving(true);
    const subscribedFlag = subscribedFlagForSave(member, role);
    try {
      await onSave?.({
        _id: member._id,
        RoleLabel: role,
        Permission: { Admin: role === "Admin" },
        Settings: { Notify: notify, Calendar: calendar },
        ...(subscribedFlag === undefined ? {} : { Subscribed: subscribedFlag }),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = (newRole: string) => {
    setLocalRole(newRole);
    saveMember(newRole, localNotify, localCalendar);
  };

  const handleNotifyChange = (checked: boolean) => {
    setLocalNotify(checked);
    saveMember(localRole, checked, localCalendar);
  };

  const handleCalendarChange = (checked: boolean) => {
    setLocalCalendar(checked);
    saveMember(localRole, localNotify, checked);
  };

  return (
    <div
      className={
        isSubscriber(member) ? "member-card is-subscriber" : "member-card"
      }
    >
      <div className="member-card-main" onClick={() => setExpanded(!expanded)}>
        <ProfileImage profile={{ url: storageDetail?.Url }} />
        <div className="member-card-info">
          <span className="member-card-name">
            {member.MemberName || "Unknown"}
          </span>
          <div className="member-card-badges">
            {/* IonBadge `color=` props were dropped: they pull
             *  --ion-color-base via :host(.ion-color) which beats
             *  any --background override on the parent class. Style
             *  contrast directly via --background / --color in CSS
             *  instead. */}
            <IonBadge
              className={
                localRole === "Admin"
                  ? "member-card-role admin-role"
                  : "member-card-role"
              }
            >
              {localRole}
            </IonBadge>
            {memberType && (
              <IonBadge className="member-card-type">{memberType}</IonBadge>
            )}
            {isSubscriber(member) && (
              <IonBadge className="member-card-subscriber">Subscriber</IonBadge>
            )}
          </div>
        </div>
        <IonIcon
          icon={expanded ? chevronUpOutline : chevronDownOutline}
          className="member-card-expand"
        />
      </div>

      {expanded && (
        <div className="member-card-details">
          {/* Top action row: Role select / read-out + Email/Phone
           *  quick-action icons + (admin-only) Remove icon. Type is
           *  intentionally NOT repeated here - the Staff/Student/
           *  Carer pill in the header above already shows it. */}
          <div className="member-card-detail-row member-card-action-row">
            <span className="detail-label">Role:</span>
            {isAdmin && onSave ? (
              <IonSelect
                value={localRole}
                onIonChange={(e) => handleRoleChange(e.detail.value)}
                interface="popover"
                className="member-role-select"
                disabled={saving}
              >
                {ROLE_OPTIONS.map((r) => (
                  <IonSelectOption key={r} value={r}>
                    {r}
                  </IonSelectOption>
                ))}
              </IonSelect>
            ) : (
              <span className="detail-value">{localRole}</span>
            )}
            {(member.Email || member.Phone) && (
              <span className="member-card-contact-actions">
                {member.Email && (
                  <a
                    href={`mailto:${member.Email}`}
                    className="member-card-contact-icon"
                    onClick={(e) => e.stopPropagation()}
                    title="Email member"
                    aria-label="Email member"
                  >
                    <IonIcon icon={mailOutline} />
                  </a>
                )}
                {member.Phone && (
                  <a
                    href={`tel:${member.Phone}`}
                    className="member-card-contact-icon"
                    onClick={(e) => e.stopPropagation()}
                    title="Call member"
                    aria-label="Call member"
                  >
                    <IonIcon icon={callOutline} />
                  </a>
                )}
              </span>
            )}
            {isAdmin && onRemove && (
              <IonButton
                fill="clear"
                color="danger"
                size="small"
                className="member-card-remove-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(member._id);
                }}
                title="Remove member"
                aria-label="Remove member"
              >
                <IonIcon slot="icon-only" icon={trashOutline} />
              </IonButton>
            )}
          </div>

          {/* Notify + Calendar toggles inline on a single row to halve
           *  the vertical space the legacy two-row layout consumed.
           *  Admin-only - non-admins can't currently flip these. */}
          {isAdmin && onSave && (
            <div className="member-card-detail-row member-card-toggles-row">
              <label className="member-card-toggle-pair">
                <span className="detail-label">Notify</span>
                <IonToggle
                  checked={localNotify}
                  onIonChange={(e) => handleNotifyChange(e.detail.checked)}
                  color="secondary"
                  disabled={saving}
                />
              </label>
              <label className="member-card-toggle-pair">
                <span className="detail-label">Calendar</span>
                <IonToggle
                  checked={localCalendar}
                  onIonChange={(e) => handleCalendarChange(e.detail.checked)}
                  color="secondary"
                  disabled={saving}
                />
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MemberCard;
